import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  SnackbarProps,
  Chip,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party

import { ColumnDef } from "@tanstack/react-table";

// project-import

import IconButton from "components/@extended/IconButton";
import { FormattedMessage } from "react-intl";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import { openSnackbar } from "api/snackbar";

import Loader from "components/Loader";

import FiltersModal from "./FiltersModal";
import { CompanyProfileModel } from "../../../../../../model/companyProfileModel";
import { USERAXIOS } from "api/userAxios";
import { FilterType } from "../../../../../../types/Filter";
import ReusableTable from "../../../../../../components/ReusableComponent/ReusableTable";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { CompanyProfileTableRow } from "../../../../../../types/company";
import { VscPackage } from "react-icons/vsc";
import { TbFileCv } from "react-icons/tb";

const CompanyProfileList = () => {
  const theme = useTheme();
  const location = useLocation();
  const [openFilters, setOpenFilters] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [customerDeleteId, setCustomerDeleteId] = useState<any>("");
  const [result, setResult] = useState<CompanyProfileTableRow[]>([]);
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const handleSearch = () => {
    setLoadingSearch(true);
    setCurrenttPage(1);
    setSearchText(tempSearchText);
    setLoadingSearch(false);
  };

  const columns = useMemo<ColumnDef<CompanyProfileModel>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="User" />,
        accessorKey: "user",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const user = cell.getValue() as any;
          return <Typography>{user?.name || "-"}</Typography>;
        },
      },

      {
        header: () => <FormattedMessage id="Company Name" />,
        accessorKey: "user",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.company_name || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="person responsible" />,
        accessorKey: "person_responsible",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const person_responsible = cell.getValue() as any;
          return <Typography>{person_responsible?.name || "-"}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Location" />,
        accessorKey: "location",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const location = cell.getValue() as any;
          return <Typography>{location?.city_name || "-"}</Typography>;
        },
      },
      {
        header: () => <FormattedMessage id="Registration Date" />,
        accessorKey: "registration_date",
        cell: ({ row, getValue }) => (
          <Stack
            direction="row"
            justifyContent="center"
            spacing={1.5}
            alignItems="center"
          >
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as number}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="packages" />,
        accessorKey: "packages",
        cell: ({ row, getValue }) => (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            <TbFileCv
              style={{
                fontSize: "1.5rem",
                cursor: "pointer",
                color: "oklch(55.6% 0 0)",
              }}
            />
            <VscPackage
              style={{
                fontSize: "1.5rem",
                cursor: "pointer",
                color: "oklch(55.6% 0 0)",
              }}
            />
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
              <Typography color="text.secondary"></Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "Status",
        cell: (cell) => {
          const status = cell.getValue();
          return (
            <Chip
              color={status === true ? "success" : "error"}
              label={
                status === true ? (
                  <FormattedMessage id="Active" />
                ) : (
                  <FormattedMessage id="Inactive" />
                )
              }
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row, getValue }) => {
          const { user_id, id } = row.original;

          const handleViewProfile = () => {
            window.location.href = `/companies-list/view-company-details/${id}`;
          };
          const handleEditProfile = () => {
            window.location.href = `companies-list/edit-company/${id}`;
          };

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="View Details">
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleViewProfile}
                >
                  <EyeOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Profile">
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleEditProfile}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setCustomerDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme]
  );

  const fetchData = () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);

    const country = urlParams.get("country_id");
    const city = urlParams.get("city_id");
    const contacted = urlParams.get("contacted");
    const client = urlParams.get("client");
    const satisfied = urlParams.get("satisfied");
    const financialAbility = urlParams.get("financial_ability");
    const hrDept = urlParams.get("hr_dept");
    const trusted = urlParams.get("trusted");
    const serviceRequired = urlParams.get("service_required");
    const ourContact = urlParams.get("our_contact");
    const culture = urlParams.get("culture");
    const lastSignIn = urlParams.get("last_sign_in");
    const postedVacs = urlParams.get("posted_vacs");

    USERAXIOS.get(
      `/company_profiles?page=${currentPage}${
        searchText ? `&title=${searchText}` : ""
      }${country ? `&country_id=${country}` : ""}${
        city ? `&city_id=${city}` : ""
      }${contacted ? `&contacted=${contacted}` : ""}${
        client ? `&client=${client}` : ""
      }${satisfied ? `&satisfied=${satisfied}` : ""}${
        financialAbility ? `&financial_ability=${financialAbility}` : ""
      }${hrDept ? `&hr_dept=${hrDept}` : ""}${
        trusted ? `&trusted=${trusted}` : ""
      }${serviceRequired ? `&service_required=${serviceRequired}` : ""}${
        ourContact ? `&our_contact=${ourContact}` : ""
      }${culture ? `&culture=${culture}` : ""}${
        lastSignIn ? `&last_sign_in=${lastSignIn}` : ""
      }${postedVacs ? `&posted_vacs=${postedVacs}` : ""}`
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching company profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching company profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page); // Update the currentPage state when the page changes
  };

  const handleFiltersClose = () => {
    setOpenFilters(false);
  };

  const clearFilters = () => {
    setLoading(true);

    const urlParams = new URLSearchParams(window.location.search);

    urlParams.delete("country_id");
    urlParams.delete("city_id");
    urlParams.delete("contacted");
    urlParams.delete("client");
    urlParams.delete("satisfied");
    urlParams.delete("financial_ability");
    urlParams.delete("hr_dept");
    urlParams.delete("trusted");
    urlParams.delete("service_required");
    urlParams.delete("our_contact");
    urlParams.delete("culture");
    urlParams.delete("last_sign_in");
    urlParams.delete("posted_vacs");

    // const newUrl = `${window.location.pathname}?${urlParams.toString()}`;

    window.history.pushState({}, "", `${window.location.pathname}`);

    USERAXIOS.get(
      `/company_profiles?page=${currentPage}${
        searchText ? `&title=${searchText}` : ""
      }`
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching company profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching company profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("country_id") ||
      urlParams.get("city_id") ||
      urlParams.get("contacted") ||
      urlParams.get("client") ||
      urlParams.get("satisfied") ||
      urlParams.get("financial_ability") ||
      urlParams.get("hr_dept") ||
      urlParams.get("trusted") ||
      urlParams.get("service_required") ||
      urlParams.get("our_contact") ||
      urlParams.get("culture") ||
      urlParams.get("last_sign_in") ||
      urlParams.get("posted_vacs")
    );
  };

  const handleFiltersApply = (filters: FilterType) => {
    setLoading(true);

    const {
      country,
      city,
      contacted,
      client,
      satisfied,
      financial_ability,
      hr_dept,
      trusted,
      service_required,
      our_contact,
      culture,
      last_sign_in,
      posted_vacs,
    } = filters;

    const urlParams = new URLSearchParams(window.location.search);
    // const companyId = urlParams.get("company_id");

    urlParams.delete("country_id");
    urlParams.delete("city_id");
    urlParams.delete("contacted");
    urlParams.delete("client");
    urlParams.delete("satisfied");
    urlParams.delete("financial_ability");
    urlParams.delete("hr_dept");
    urlParams.delete("trusted");
    urlParams.delete("service_required");
    urlParams.delete("our_contact");
    urlParams.delete("culture");
    urlParams.delete("last_sign_in");
    urlParams.delete("posted_vacs");

    if (country) urlParams.set("country_id", country);
    if (city) urlParams.set("city_id", city);
    if (contacted) urlParams.set("contacted", contacted);
    if (client) urlParams.set("client", client);
    if (satisfied) urlParams.set("satisfied", satisfied);
    if (financial_ability)
      urlParams.set("financial_ability", financial_ability);
    if (hr_dept) urlParams.set("hr_dept", hr_dept);
    if (trusted) urlParams.set("trusted", trusted);
    if (service_required) urlParams.set("service_required", service_required);
    if (our_contact) urlParams.set("our_contact", our_contact);
    if (culture) urlParams.set("culture", culture);
    if (last_sign_in) urlParams.set("last_sign_in", last_sign_in);
    if (posted_vacs) urlParams.set("posted_vacs", posted_vacs);

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    //${companyId ? `&company_id=${companyId}` : ""}

    USERAXIOS.get(
      `/company_profiles?page=${currentPage}${
        country ? `&country_id=${country}` : ""
      }${city ? `&city_id=${city}` : ""}${
        contacted ? `&contacted=${contacted}` : ""
      }${client ? `&client=${client}` : ""}${
        satisfied ? `&satisfied=${satisfied}` : ""
      }${financial_ability ? `&financial_ability=${financial_ability}` : ""}${
        hr_dept ? `&hr_dept=${hr_dept}` : ""
      }${trusted ? `&trusted=${trusted}` : ""}${
        service_required ? `&service_required=${service_required}` : ""
      }${our_contact ? `&our_contact=${our_contact}` : ""}${
        culture ? `&culture=${culture}` : ""
      }${last_sign_in ? `&last_sign_in=${last_sign_in}` : ""}${
        posted_vacs ? `&posted_vacs=${posted_vacs}` : ""
      }${searchText ? `&title=${searchText}` : ""}`
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching company profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching company profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await USERAXIOS.delete(`/company_profiles/${customerDeleteId}`);
      setResult((prevResults) =>
        prevResults.filter((item) => item.id !== customerDeleteId)
      );
      handleClose();
      openSnackbar({
        open: true,
        message: "Company profile deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);
    } catch (error) {
      console.error("Error deleting company profile:", error);
      openSnackbar({
        open: true,
        message: "Failed to delete company profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            {hasActiveFilters() && (
              <Button
                variant="outlined"
                color="primary"
                onClick={clearFilters}
                sx={{ mb: 2, padding: ".3rem 2rem " }}
              >
                <FormattedMessage id="Clear Filters" />
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<FilterOutlined />}
              onClick={() => setOpenFilters(true)}
              sx={{
                mb: 2,
                padding: ".3rem 2rem",
                gap: ".5rem",
              }}
            >
              <FormattedMessage id="Filters" />
            </Button>
          </Box>
          <ReusableTable
            data={result}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearch}
            searchedtext={searchText}
            setSearchedtext={setSearchText}
            modalToggler={() => {
              window.location.href = `companies-list/add-company`;
              // setCustomerModal(true);
              // setSelectedCustomer(null);
            }}
            renderDetailPanel={({ row }: any) => <></>}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersClose}
          onApplyFilters={handleFiltersApply}
          onClearFilters={clearFilters}
        />
      )}

      <ReusableDeleteDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default CompanyProfileList;
